import React from 'react';
import { useTranslation } from 'react-i18next';
import { mapModifiers } from 'src/libs/component';

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  title: string;
  required?: boolean;
  errorMessage?: string;
  modifiers?: 'search';
  className?: string;
  containerClassName?: string;
  noWrap?: boolean;
  isClearable?: boolean;
  ref?: React.Ref<HTMLSelectElement>;
}

export const Select: React.FC<SelectProps> = React.forwardRef(
  ({ children, title, required, modifiers, errorMessage, className, containerClassName, noWrap, isClearable = false, value, onChange, ...selectProps }, ref) => {
    const { t } = useTranslation();

    // Debug log
    //console.log('Select props:', { isClearable, value, title });

    const handleClear = () => {
      if (onChange) {
        onChange({ target: { value: '' } } as React.ChangeEvent<HTMLSelectElement>);
      }
    };

    return (
      <div className={`${mapModifiers('a-select', errorMessage && 'error', noWrap && 'no-wrap', modifiers, isClearable && 'clearable')} ${containerClassName || ''}`}>
        {noWrap && (
          <div className="a-select__title-noWrap">
            <label>{title}</label>
            {required ? <span className="a-select__requied">{t('common.mandatory')}</span> : null}
          </div>
        )}
        <div className="a-select__wrapper" style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
          <select {...selectProps} className={`a-select__input ${className ? className : ''}`} ref={ref} value={value} onChange={onChange}>
            {children}
          </select>
          {isClearable && value && value !== '' && !selectProps.disabled && (
            <button
              type="button"
              className="a-date-input__clear"
              onClick={handleClear}
              aria-label="Clear selection"
              style={{
                position: 'absolute',
                top: '22px',
                right: '10px',
                width: '15px',
                height: '15px',
                color: 'white',
                border: 'none',
                background: 'gray',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                cursor: 'pointer',
              }}
            >
              x
            </button>

          )}
        </div>
        {
          !noWrap && (
            <div className="a-select__title">
              <label>{title}</label>
              {required ? <span className="a-select__requied">{t('common.mandatory')}</span> : null}
            </div>
          )
        }
        {errorMessage && <p className="a-select__error-message">{t(errorMessage)}</p>}
      </div >
    );
  }
);

Select.displayName = 'Select';

export interface OptionGroupProps extends Omit<React.OptionHTMLAttributes<HTMLOptionElement>, 'className'> {
  label?: string;
  modifiers?: 'select';
}

export const OptionGroup: React.FC<OptionGroupProps> = ({ children, modifiers, label, ...optionProps }) => (
  <optgroup {...optionProps} className={mapModifiers('a-option', modifiers)} label={label}>
    {children}
  </optgroup>
);

export interface OptionProps extends Omit<React.OptionHTMLAttributes<HTMLOptionElement>, 'className'> {
  value?: string | number;
  modifiers?: 'select';
}

export const Option: React.FC<OptionProps> = ({ children, modifiers, value, ...optionProps }) => (
  <option {...optionProps} className={mapModifiers('a-option', modifiers)} value={value}>
    {children}
  </option>
);
